import React, { useEffect, useMemo, useRef, useState } from "react";
import "./css/issuance.css";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";


export default function Issuance({ cert: certProp }) {
    const navigate = useNavigate();
    const location = useLocation();

    // 전화번호 하이픈 포맷
    const formatPhoneKOR = (input = "") => {
        const v = String(input).replace(/\D/g, "");
        if (!v) return "";
        if (v.startsWith("02")) {
            if (v.length <= 2) return v;
            if (v.length <= 5) return `02-${v.slice(2)}`;
            if (v.length <= 9) return `02-${v.slice(2, 5)}-${v.slice(5)}`;
            return `02-${v.slice(2, 6)}-${v.slice(6, 10)}`;
        }
        const head3 = v.slice(0, 3);
        if (v.length <= 3) return head3;
        if (v.length <= 7) return `${head3}-${v.slice(3)}`;
        if (v.length <= 10) return `${head3}-${v.slice(3, 6)}-${v.slice(6, 10)}`;
        return `${head3}-${v.slice(3, 7)}-${v.slice(7, 11)}`;
    };

    // 사용자 정보 상태
    const [userInfo, setUserInfo] = useState({
        nickname: "",        // 닉네임
        phone: "",           // 전화번호
        church: "",          // 소속
        position: "",        // (사용 안 함 - 기존 호환용)
        yearAssociation: "", // (사용 안 함 - 기존 호환용)
        localAssociation: "",// (사용 안 함 - 기존 호환용)
    });

    // 이미지 업로드 상태
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    // 이미지 파일을 압축하여 base64로 반환 (용량 절감을 위해)
    const compressImageToBase64 = (file, maxWidth = 640, maxHeight = 640, quality = 0.7) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (ev) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    let { width, height } = img;
                    // 비율 유지하며 리사이즈
                    if (width > maxWidth || height > maxHeight) {
                        const ratio = Math.min(maxWidth / width, maxHeight / height);
                        width = Math.round(width * ratio);
                        height = Math.round(height * ratio);
                    }
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0, width, height);
                    const mimeType = file.type && file.type.startsWith("image/") ? file.type : "image/jpeg";
                    const dataUrl = canvas.toDataURL(mimeType, quality);
                    resolve(dataUrl);
                };
                img.onerror = reject;
                img.src = ev.target.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // 이미지 파일 선택 핸들러 (압축 후 저장)
    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // 파일 크기 체크 (5MB 제한)
        if (file.size > 5 * 1024 * 1024) {
            setMessage({ msg: "이미지 크기는 5MB 이하여야 합니다." });
            return;
        }

        // 파일 타입 체크
        if (!file.type.startsWith('image/')) {
            setMessage({ msg: "이미지 파일만 업로드 가능합니다." });
            return;
        }

        try {
            const compressedBase64 = await compressImageToBase64(file, 640, 640, 0.7);
            setImageFile(file);
            setImagePreview(compressedBase64);
        } catch (err) {
            console.error("이미지 압축 실패:", err);
            setMessage({ msg: "이미지 처리 중 오류가 발생했습니다." });
        }
    };

    // 이미지 제거 핸들러
    const handleImageRemove = () => {
        setImageFile(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // 1) 자격증 정보: props 우선, 없으면 location.state.cert, 둘 다 없으면 기본값
    const cert = useMemo(() => {
        return (
            certProp ||
            location.state?.cert || {
                number: "2025-12-123",
                name: "탐정법인홍익 모바일 신분증",
                issuer: "탐정법인홍익",
            }
        );
    }, [certProp, location.state]);

    // 모바일 신분증 여부 확인
    const isMobileId = cert.name === "탐정법인홍익 모바일 신분증";
    const flowMode = location.state?.mode || null; // join | join-edit | null
    const redirectTo = location.state?.redirectTo;
    const isJoinFlow = (isMobileId && (flowMode === "join" || flowMode === "join-edit")) || (!isMobileId && flowMode === "join");
    const isEditJoinFlow = isMobileId && flowMode === "join-edit";

    // 등급 표시 (level에 따라)
    const getGradeText = (level) => {
        if (level === 1) return "정회원";
        if (level === 2) return "준회원";
        return level ? `등급 ${level}` : "-";
    };

    const [message, setMessage] = useState(null);

    // 신청 완료 모달
    const [submitOpen, setSubmitOpen] = useState(false);

    // 필수 필드 검증
    const validateFields = () => {
        if (!userInfo.nickname || userInfo.nickname.trim() === "") {
            setMessage({ msg: "닉네임을 입력해주세요." });
            return false;
        }
        if (!userInfo.phone || userInfo.phone.trim() === "") {
            setMessage({ msg: "전화번호를 입력해주세요." });
            return false;
        }
        if (!userInfo.church || userInfo.church.trim() === "") {
            setMessage({ msg: "소속을 입력해주세요." });
            return false;
        }
        return true;
    };

    // 제출(신청) 버튼
    const onSubmit = (e) => {
        e.preventDefault();

        // 필수 필드 검증
        if (!validateFields()) {
            return;
        }

        // TODO: 실제 신청 로직 연결

        // 이미지를 base64로 변환
        let imageBase64 = null;
        if (imageFile && imagePreview) {
            imageBase64 = imagePreview;
        }

        if (isMobileId) {
            if (isJoinFlow) {
                // 가맹점 가입/수정
                const joinData = {
                    name: "탐정법인홍익 모바일 신분증",
                    number: cert.number || "",
                    issuer: cert.issuer || "탐정법인홍익",
                    nickname: userInfo.nickname,
                    phone: userInfo.phone ? userInfo.phone.replace(/\D/g, "") : "",
                    position: userInfo.position,
                    yearAssociation: userInfo.yearAssociation,
                    localAssociation: userInfo.localAssociation,
                    church: userInfo.church, // 소속
                    image: imageBase64,
                    joinedAt: new Date().toISOString(),
                };

                try {
                    localStorage.setItem("hb_mobile_id_joined", "true");
                    localStorage.setItem("hb_mobile_join_info", JSON.stringify(joinData));
                    // 가입 수정 시 기존 대기 신청은 제거
                    localStorage.removeItem("hb_pending_cert");
                } catch (err) {
                    console.error("localStorage quota error (hb_mobile_join_info):", err);
                    setMessage({ msg: "저장 공간이 부족합니다. 이미지 크기를 줄여주세요." });
                    return;
                }
            } else {
                // 모바일 신분증 신청
                const applicationData = {
                    name: "탐정법인홍익 모바일 신분증",
                    number: cert.number || "",
                    issuer: cert.issuer || "탐정법인홍익",
                    level: 1,
                    nickname: userInfo.nickname,
                    phone: userInfo.phone ? userInfo.phone.replace(/\D/g, "") : "",
                    position: userInfo.position,
                    yearAssociation: userInfo.yearAssociation,
                    localAssociation: userInfo.localAssociation,
                    church: userInfo.church, // 소속
                    image: imageBase64,
                    status: "pending",
                    appliedAt: new Date().toISOString(),
                };
                try {
                    localStorage.setItem("hb_pending_cert", JSON.stringify(applicationData));
                    localStorage.setItem("hb_mobile_id_joined", "true");
                } catch (err) {
                    console.error("localStorage quota error (hb_pending_cert):", err);
                    setMessage({ msg: "저장 공간이 부족합니다. 이미지 크기를 줄여주세요." });
                    return;
                }
            }
        } else {
            // 다른 자격증 가입/신청
            if (flowMode === "join") {
                // 가맹점 가입만 (자격증 신청은 하지 않음)
                const joinData = {
                    name: cert.name,
                    org: cert.org || cert.issuer || "",
                    nickname: userInfo.nickname,
                    phone: userInfo.phone ? userInfo.phone.replace(/\D/g, "") : "",
                    position: userInfo.position,
                    yearAssociation: userInfo.yearAssociation,
                    localAssociation: userInfo.localAssociation,
                    church: userInfo.church, // 소속
                    image: imageBase64,
                    joinedAt: new Date().toISOString(),
                };
                
                // 가입 정보만 저장
                try {
                    localStorage.setItem("hb_general_joined", "true");
                    localStorage.setItem("hb_general_join_info", JSON.stringify(joinData));
                } catch (err) {
                    console.error("localStorage quota error (hb_general_join_info):", err);
                    setMessage({ msg: "저장 공간이 부족합니다. 이미지 크기를 줄여주세요." });
                    return;
                }
            } else {
                // 일반 신청 (가입 완료 후 신청)
                const applicationData = {
                    name: cert.name,
                    number: cert.number || "",
                    issuer: cert.issuer || cert.org || "",
                    level: 1,
                    nickname: userInfo.nickname,
                    phone: userInfo.phone ? userInfo.phone.replace(/\D/g, "") : "",
                    position: userInfo.position,
                    yearAssociation: userInfo.yearAssociation,
                    localAssociation: userInfo.localAssociation,
                    church: userInfo.church, // 소속
                    image: imageBase64,
                    status: "pending",
                    appliedAt: new Date().toISOString(),
                };

                // 기존 자격증 목록 가져오기
                const existingCerts = JSON.parse(localStorage.getItem("hb_pending_certs") || "[]");
                existingCerts.push(applicationData);
                try {
                    localStorage.setItem("hb_pending_certs", JSON.stringify(existingCerts));
                } catch (err) {
                    console.error("localStorage quota error (hb_pending_certs):", err);
                    setMessage({ msg: "저장 공간이 부족합니다. 이미지 크기를 줄여주세요." });
                    return;
                }
            }
        }

        setSubmitOpen(true);
    };

    // 신청 완료 후 이동할 슬라이드 인덱스 계산
    const getTargetSlideIndex = () => {
        if (isMobileId) {
            return 3; // 모바일 신분증은 인덱스 3 (+ 카드(0), 메인 신분증(1), 탐정법인홍익 모바일 신분증(2), 신청한 카드(3))
        } else {
            // 다른 자격증은 맨 뒤로
            const existingCerts = JSON.parse(localStorage.getItem("hb_pending_certs") || "[]");
            // + 카드(0) + 메인 신분증(1) + 탐정법인홍익 모바일 신분증 대기(1, 있을 경우) + 기존 자격증들
            const mobileIdPending = localStorage.getItem("hb_pending_cert") ? 1 : 0;
            return 2 + mobileIdPending + existingCerts.length; // 새 자격증이 추가되기 전 개수이므로 +1 필요 없음
        }
    };

    return (
        <div className="container-wrap main-wrap issuance-wrap">
            <div className="issuance-viewport">
                {/* 로고 헤더 */}
                <header className="issuance-header ">
                    <div
                        style={{
                            position: "absolute",
                            left: "15px",
                            top: "14px",
                            padding: "10px",
                            cursor: "pointer",
                            zIndex: 10
                        }}
                        onClick={() => navigate(-1)}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: "20px", color: "#333" }} />
                    </div>
                    <img
                        src="/img/hongik_logo.png"
                        alt="탐정법인홍익"
                        className="issuance-logo"
                    // onClick={() => navigate("/")}
                    />
                </header>

                {/* 폼 */}
                <form className="issuance-form" onSubmit={onSubmit}>
                    <h1 className="issuance-title" style={{}}>
                        {isMobileId
                            ? isEditJoinFlow
                                ? "가입수정"
                                : "가입신청서"
                            : flowMode === "join"
                                ? "가입신청서"
                                : "발급신청"}
                    </h1>

                    {/* 자격증 정보(읽기전용) - 가입 흐름이 아닐 때만 표시 */}
                    {
                        !isMobileId && flowMode !== "join" &&
                        <div className="issuance-card">
                            <div className="issuance-row">
                                <label>자격증</label>
                                <div className="issuance-value">{cert.name || "탐정법인홍익 모바일 신분증"}</div>
                            </div>
                            <div className="issuance-row">
                                <label>번호</label>
                                <div className="issuance-value">{cert.number || "-"}</div>
                            </div>
                            <div className="issuance-row" style={{ alignItems: "flex-start" }}>
                                <label>발급기관</label>
                                <div
                                    className="issuance-value"
                                    style={{ fontSize: "14px", whiteSpace: "pre-line" }}
                                >
                                    {cert.issuer || "탐정법인홍익"}
                                </div>
                            </div>
                        </div>
                    }

                    {/* 상세정보: 닉네임, 전화번호, 소속 + 이미지 */}
                    <div className="issuance-section">
                        <div className="issuance-row">
                            <label htmlFor="nickname">닉네임</label>
                            <input
                                id="nickname"
                                className="issuance-input"
                                type="text"
                                value={userInfo.nickname}
                                onChange={(e) => setUserInfo((p) => ({ ...p, nickname: e.target.value }))}
                                placeholder="닉네임을 입력하세요"
                            />
                        </div>
                        <div className="issuance-row">
                            <label htmlFor="phone">전화번호</label>
                            <input
                                id="phone"
                                className="issuance-input"
                                type="tel"
                                value={userInfo.phone}
                                onChange={(e) => setUserInfo((p) => ({ ...p, phone: formatPhoneKOR(e.target.value) }))}
                                placeholder="010-1234-5678"
                            />
                        </div>
                        <div className="issuance-row">
                            <label htmlFor="affiliation">소속</label>
                            <input
                                id="affiliation"
                                className="issuance-input"
                                type="text"
                                value={userInfo.church}
                                onChange={(e) => setUserInfo((p) => ({ ...p, church: e.target.value }))}
                                placeholder="소속을 입력하세요"
                            />
                        </div>
                        <div className="issuance-row" style={{ alignItems: "flex-start" }}>
                            <label htmlFor="image">이미지</label>
                            <div className="issuance-image-container" style={{ width: "100%" }}>
                                {imagePreview ? (
                                    <div className="issuance-image-preview-wrapper">
                                        <img
                                            src={imagePreview}
                                            alt="미리보기"
                                            className="issuance-image-preview"
                                        />
                                        <button
                                            type="button"
                                            className="issuance-image-remove"
                                            onClick={handleImageRemove}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <div className="issuance-image-upload-area">
                                        <input
                                            ref={fileInputRef}
                                            id="image"
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            style={{ display: "none" }}
                                        />
                                        <label
                                            htmlFor="image"
                                            className="issuance-image-upload-button"
                                        >
                                            <span>+</span>
                                            <span>이미지 선택</span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </form>

                {/* 하단 고정 버튼 */}
                <div className="issuance-button-container">
                    <button
                        type="submit"
                        className="issuance-pay"
                        title={isJoinFlow ? "가입신청" : "신청하기"}
                        onClick={(e) => {
                            e.preventDefault();
                            const form = document.querySelector('.issuance-form');
                            if (form) {
                                form.requestSubmit();
                            }
                        }}
                    >
                        {isJoinFlow ? "가입신청" : "신청하기"}
                    </button>
                </div>

                {/* 하단 풋터 영역 고려(고정 풋터 120px) */}
                {/* <div style={{ height: 100 }} /> */}
            </div>

            {/* 신청 완료 모달 */}
            {submitOpen && (
                <div className="main-modal" role="dialog" aria-modal="true" aria-labelledby="submit-title" onClick={() => {
                    setSubmitOpen(false);
                    if (isJoinFlow) {
                        navigate(redirectTo || "/search", { replace: true });
                        return;
                    }
                    const targetIndex = getTargetSlideIndex();
                    navigate("/main", { state: { slideIndex: targetIndex } });
                }}>
                    <div className="main-modal-dialog" onClick={(e) => e.stopPropagation()}>
                        <div className="main-modal-body">
                            <h3 id="submit-title" className="main-modal-title">
                                {isJoinFlow
                                    ? isEditJoinFlow
                                        ? "가입수정이 완료되었습니다."
                                        : "가입이 완료되었습니다."
                                    : "신청이 완료되었습니다."}
                            </h3>
                            <p className="main-modal-desc">
                                {isJoinFlow
                                    ? isEditJoinFlow
                                        ? "입력한 내용으로 가입 정보가 수정되었습니다."
                                        : "정상적으로 가입이 완료되었습니다."
                                    : "정상적으로 신청이 처리되었습니다."}
                            </p>
                        </div>
                        <div className="main-modal-actions">
                            <button className="main-modal-btn" onClick={() => {
                                setSubmitOpen(false);
                                if (isJoinFlow) {
                                    navigate(redirectTo || "/search", { replace: true });
                                } else {
                                    const targetIndex = getTargetSlideIndex();
                                    navigate("/main", { state: { slideIndex: targetIndex } });
                                }
                            }}>확인</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 간단 메시지 토스트 */}
            {message?.msg && (
                <div className="issuance-toast" onAnimationEnd={() => setMessage(null)}>
                    {message.msg}
                </div>
            )}
        </div>
    );
}
